import {ReactNode} from 'react';

import {makeEnumTypeGuard} from '../../../../libs/shared/core/src/models/api/types/enumTypes';
import {AnalyticsMetricsKeys} from '../../../models/api/analyticsMetricsModel';
import {AnalyticsReportFilterOptions} from '../../../models/api/analyticsSavedViewModel';

interface ReportMetricConfig<T extends AnalyticsMetricsKeys> {
  id: T;
  isDisplayedByDefault: boolean;
  isOnClickDisabled?: boolean;
  category?: MetricCategoriesEnum;
}

export interface ReportConfigElement<T extends AnalyticsMetricsKeys = AnalyticsMetricsKeys> {
  id: ReportElementIdsEnum;
  // All metrics available on that report element. The order will be used in metric picker dropdowns.
  metrics: ReadonlyArray<ReportMetricConfig<T>>;
  // For table metrics, they need to define which columns should be displayed.
  columns?: ReadonlyArray<ReportMetricConfig<T>>;
}

export interface AnalyticsReportConfig {
  title: ReactNode;
  reportElements: ReadonlyArray<ReportConfigElement>;
  options?: AnalyticsReportFilterOptions;
  isBeta?: boolean;
  isNew?: boolean;
}

export enum ReportFilterOptionsEnum {
  /*
   * The `customers_table` metric, by default, returns a mix of card IDs and contact IDs. This option forces it to
   * always return contact IDs.
   */
  ALWAYS_EMIT_CONTACT_ID = 'always_emit_contact_id',
  BUCKET_BOUNDARIES = 'bucket_boundaries', // Set custom buckets for donut chart.
  COLLECT_CONTRIBUTING_SEGMENT_IDS = 'collect_contributing_segment_ids',
  SELECTED_METRICS = 'selected_metrics',
  TAG_MODE = 'tag_mode', // Is the tag still applied or was it applied during period?
  /*
   * By explicitly specifying which metrics we need to have calculated for the previous period we can avoid doing so
   * unnecessarily.
   */
  PREVIOUS_PERIOD_METRICS = 'previous_period_metrics',
}

export enum ContributingSegmentsOptionsEnum {
  ROW = 'row',
  SUB_METRIC = 'sub_metric',
}

export interface BasicReportProps {
  isLoading: boolean;
}

export enum AnalyticsReportsEnum {
  // V3 Reports.
  WORKLOAD = 'workload',
  // Legacy reports.
  OVERVIEW = 'overview',
  TEAM_PERF = 'team_performance',
  CONVERSATIONS = 'conversations',
  TAGS = 'tags',
  MESSAGE_TEMPLATES = 'message_templates',
  SLA = 'sla',
  CUSTOMER_EXPERIENCE = 'customer_experience',
}

export enum AnalyticsAutomationReportsEnum {
  CHATBOTS = 'chatbots',
  KNOWLEDGE_BASE = 'knowledge_base',
}

export enum ReportElementIdsEnum {
  // WORKLOAD
  WORKLOAD_KEY_METRICS = 'workload-key-metrics',
  WORKLOAD_OVER_TIME = 'workload-over-time',
  WORKLOAD_BUSIEST_TIMES = 'workload-busiest-times',
  WORKLOAD_EFFICIENCY = 'workload-efficiency',
  WORKLOAD_CONVERSATION_STAGES = 'workload-conversation-stages',
  WORKLOAD_BY_INBOX = 'workload-by-inbox',
  // WORKLOAD POC
  WORKLOAD_POC_METRICS_CARDS = 'workload-poc-metrics-cards',
  WORKLOAD_POC_OVER_TIME = 'workload-poc-over-time',
  WORKLOAD_POC_STAGE_RESOLVED_PCT = 'workload-poc-stage-resolved-pct',
  WORKLOAD_POC_BY_INBOX = 'workload-poc-by-inbox',
  WORKLOAD_POC_HEATMAP = 'workload-poc-heatmap',
  WORKLOAD_POC_EFFICIENCY = 'workload-poc-efficiency',
  WORKLOAD_POC_DONUT = 'workload-poc-donut',
  WORKLOAD_POC_HISTOGRAM = 'workload-poc-histogram',
  // OVERVIEW
  OVERVIEW_METRICS_CARDS = 'overview-metrics-cards',
  OVERVIEW_RESOLVED_PCT = 'resolved-segments-pct',
  OVERVIEW_STAGE_RESOLVED_PCT = 'stage-resolved-segments-pct',
  OVERVIEW_CHANGES_OVER_TIME = 'overview-changes-over-time',
  OVERVIEW_REPLY_TIMES_WRAPPER = 'reply-times-teammates-and-tags',
  OVERVIEW_REPLY_TIMES_DONUT = 'reply-times-donut',
  OVERVIEW_REPLY_TIMES_HISTOGRAM = 'reply-time-histogram',
  OVERVIEW_TEAM_CHANGES_HISTOGRAM = 'team-changes-list',
  OVERVIEW_TAGS_HISTOGRAM = 'top-tags-list',
  // TEAM PERF
  TEAM_METRICS_CARDS = 'team-metrics-cards',
  TEAM_HEATMAP = 'busiest-times',
  TEAM_TEAMMATES_TABLE = 'teammates-table',
  // CONVERSATIONS
  CONVERSATIONS_WHAT_HAPPENED = 'what-happened',
  CONVERSATIONS_SANKEY = 'conversation-flows',
  CONVERSATIONS_INBOXES = 'inboxes',
  // TAGS
  TAGS_CONVERSATIONS_PCT = 'conversation-with-tags',
  TAGS_PERFORMANCE_TABLE = 'individual-tag-performance',
  TAG_CHANGES_TABLE = 'tag-changes',
  // MESSAGE TEMPLATES
  MESSAGE_TEMPLATES_PCT = 'messages-with-templates',
  MESSAGE_TEMPLATE_TABLE = 'message-template-table',
  // SLA
  SLA_METRICS_CARDS = 'sla-metrics-cards',
  SLA_PERF_GRAPH = 'sla-performance-graph',
  SLA_PERF_GRAPH_V2 = 'sla-performance-graph-v2',
  SLA_STATUS_TABLE_V2 = 'sla-status-table-v2',
  // CUSTOMER EXPERIENCE
  CX_TOTAL_SCALAR = 'customer-experience-total-scalar',
  CX_SCORES_DONUT_WRAPPER = 'customer-experience-scores-donut',
  CX_SCORES_LIST_WRAPPER = 'customer-experience-scores-list',
  CX_SCORES_AVG = 'customer-experience-scores-average',
  CX_CHANGES_OVER_TIME_CHART = 'customer-experience-changes-over-time',
  CX_SAMPLE_FEEDBACK_TABLE = 'customer-experience-report-sample-feedback-table',
  CX_TABLE = 'customer-experience-report-table',
  CX_CUSTOMERS_TABLE_V2 = 'customer-experience-report-customers-table-v2',
  CX_ACCOUNTS_TABLE = 'customer-experience-report-accounts-table',
  // CHATBOT/AI ANALYTICS
  CHATBOT_BASE_ANALYTICS = 'automation-reports-chatbot-base-analytics',
  CHATBOT_AI_ANALYTICS = 'automation-reports-chatbot-ai-analytics',
  // KNOWLEDGE BASE
  KNOWLEDGE_BASE_METRIC_CARDS = 'knowledge-base-metric-cards',
  KNOWLEDGE_BASE_CHANGES_OVER_TIME = 'knowledge-base-changes-over-time',
  KNOWLEDGE_BASE_PAGE_VIEWS_TABLE = 'knowledge-base-page-views-table',
  KNOWLEDGE_BASE_SEARCH_TERMS_TABLE = 'knowledge-base-search-terms-table',
}

export enum MetricCategoriesEnum {
  VOLUME = 'volume',
  EFFICIENCY = 'efficiency',
  WORKFORCE = 'workforce',
}

export const isAnalyticsReportsEnum = makeEnumTypeGuard(AnalyticsReportsEnum);
export const isAnalyticsAutomationReportsEnum = makeEnumTypeGuard(AnalyticsAutomationReportsEnum);
